import { get, find, findIndex, findLast } from 'lodash';
import {
  QUESTION_SINGLE,
  QUESTION_MULTIPLE,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
  QUESTION_MATRIX_CONSTANT_SUM,
  QUESTION_RANKING,
  QUESTION_RATING,
  QUESTION_NPS,
  QUESTION_DRILLDOWN,
  NODE_TYPES_NOT_ALLOWED_LOGIC,
} from 'data/nodeTypes';
import { npsOptions } from 'containers/QuestionnairePage/constants';
import { findChildrenOnDepth } from 'containers/QuestionnairePage/GenericQuestion/DrillDownQuestion/utils';
import { NODE_FEATURE_SUPPORT } from 'data/featuresPerQuestion';
import {
  FEATURE_ADVANCED_LOGIC,
  FEATURE_QUOTAS_ADVANCED,
} from 'data/questionFeatures';
import {
  getDefaultOutcomeAction,
  getOutcomeType,
} from 'components/LogicOutcomeWithActions/utils';
import { getTargetLogicExtended } from 'containers/QuestionnaireLogic/utils';
import { canHaveConditionalDisplayLogic } from 'components/VisibilityActionButton/utils';
import { FinalGroupNode } from 'utils/adl-services';

export const getDefaultConditionAnswer = (question, columnRefId) => {
  switch (question.type) {
    case QUESTION_SINGLE:
    case QUESTION_MULTIPLE: {
      const validAnswer = find(
        question.answers,
        (answer) => answer.body && answer.allowSelection,
      );
      const firstAnswer = get(question, 'answers[0]');
      return get(validAnswer || firstAnswer, 'refId');
    }

    case QUESTION_MATRIX_SINGLE:
    case QUESTION_MATRIX_MULTIPLE: {
      const validRow = find(
        question.answers,
        (answer) => answer.body && answer.allowSelection,
      );
      const validColumn = find(
        question.scorepoints,
        (scorepoint) => scorepoint.body && scorepoint.allowSelection,
      );
      const firstRow = get(question, 'answers[0]');
      const firstColumn = get(question, 'scorepoints[0]');
      const row = get(validRow || firstRow, 'refId');
      const column = get(validColumn || firstColumn, 'refId');
      return { row, column };
    }
    case QUESTION_MATRIX_BIPOLAR: {
      // TODO: RES-5957
      const { matrix = {} } = question;
      // TODO: use the answer inside the questions
      // instead of the matrixAnswers, and use the body to find
      // the one with a body
      const {
        matrixAnswers = [],
        // columns = [],
        // style
      } = matrix;
      const answers = matrixAnswers
        .filter(({ allowSelection }) => allowSelection)
        .map(({ answerRefId }) => answerRefId);
      const row = answers[0];
      return { row, number: undefined };
    }
    case QUESTION_MATRIX_OPEN_ENDED:
    case QUESTION_MATRIX_NUMERIC_OPEN_ENDED:
    case QUESTION_MATRIX_CONSTANT_SUM: {
      const { matrix = {} } = question;
      const { matrixAnswers = [], columns = [] } = matrix;

      const answers = matrixAnswers
        .filter(({ allowSelection }) => allowSelection)
        .map(({ answerRefId }) => answerRefId);
      const row = answers[0];
      const column = columns
        .filter(({ allowSelection }) => allowSelection)
        .map(({ refId }) => refId)[0];

      if (question.type === QUESTION_MATRIX_OPEN_ENDED) {
        return { row, column, text: undefined };
      }

      return { row, column, number: undefined };
    }
    // Ranking questions' columns are just numbers
    case QUESTION_RANKING: {
      const validRow = find(
        question.answers,
        (answer) => answer.body && answer.allowSelection,
      );
      const firstRow = get(question, 'answers[0]');
      const row = get(validRow || firstRow, 'refId');
      // if there are no rows, then there is no reason to select 1 as a value, better leave it empty
      const column = row ? '1' : undefined;
      return { row, column };
    }
    case QUESTION_RATING: {
      return 1;
    }
    case QUESTION_NPS:
      return get(npsOptions, '[0].value');
    case QUESTION_DRILLDOWN: {
      const drillDownLevels = get(
        question,
        'matrix.validation.drillDownConfigurationDto.config.levels',
      );
      const columnIndex = columnRefId
        ? findIndex(
            question.matrix.columns,
            (column) => column.refId === columnRefId,
          )
        : 0;
      const children = findChildrenOnDepth(
        { children: drillDownLevels },
        columnIndex,
      );

      return get(children[0], 'value');
    }
    default:
      return '';
  }
};

export const getDefaultConcept = (node) => get(node, 'concepts[0].refId');

export const getClosestNotAllowedContext = ({ nodes, node }) => {
  if (!NODE_TYPES_NOT_ALLOWED_LOGIC.includes(node.type)) {
    return node;
  }

  const currentQuestionIndex = findIndex(nodes, ['refId', node.refId]);
  let closestAvailable = findLast(
    nodes,
    (questionToCheck, index) =>
      index < currentQuestionIndex &&
      !NODE_TYPES_NOT_ALLOWED_LOGIC.includes(questionToCheck.type),
  );
  if (!closestAvailable) {
    closestAvailable = find(
      nodes,
      (questionToCheck, index) =>
        index > currentQuestionIndex &&
        !NODE_TYPES_NOT_ALLOWED_LOGIC.includes(questionToCheck.type),
    );
  }
  return closestAvailable;
};

export const calculateNodeLogicFlags = ({
  logicMode,
  nodeType,
  isScreening,
  isNodeHidden,
  hasSimpleQuotas,
  hasMinimumResponses,
  node,
  hasQuestionnaireShufflingEnabled,
}) => {
  if (isNodeHidden) {
    return {
      canAddLogic: false,
    };
  }

  const { isDisqualificationLogic, isDisplayLogic, isAdvancedQuotaLogic } =
    getTargetLogicExtended(logicMode);
  const {
    hasParentNodeWithEnabledShuffling,
    inDisplaySamePageGroup,
    isInVanWestendorpGroup,
  } = node;

  if (isDisplayLogic) {
    if (hasQuestionnaireShufflingEnabled) {
      return { canAddLogic: false };
    }
    if (hasSimpleQuotas || hasMinimumResponses) {
      return { canAddLogic: false };
    }
    return {
      canAddLogic: canHaveConditionalDisplayLogic(
        isScreening,
        isInVanWestendorpGroup,
      ),
    };
  }
  if (isAdvancedQuotaLogic) {
    const { isInAb, isInLoopAndMerge } = node;

    const cannotAddRuleForAdvancedQuotas =
      isInAb ||
      isInLoopAndMerge ||
      isInVanWestendorpGroup ||
      hasParentNodeWithEnabledShuffling ||
      inDisplaySamePageGroup ||
      isScreening ||
      hasQuestionnaireShufflingEnabled;

    const canAddAdvancedQuotasButHasSimple =
      !cannotAddRuleForAdvancedQuotas &&
      NODE_FEATURE_SUPPORT[nodeType]?.includes(FEATURE_QUOTAS_ADVANCED) &&
      hasSimpleQuotas;

    const canAddAdvancedQuotasButHasMinimumResponses =
      !cannotAddRuleForAdvancedQuotas &&
      NODE_FEATURE_SUPPORT[nodeType]?.includes(FEATURE_QUOTAS_ADVANCED) &&
      hasMinimumResponses;

    return {
      canAddLogic: !cannotAddRuleForAdvancedQuotas,
      canAddAdvancedQuotasButHasSimple,
      canAddAdvancedQuotasButHasMinimumResponses,
    };
  }

  const isNodeSupportingLogic =
    !isScreening &&
    NODE_FEATURE_SUPPORT[nodeType]?.includes(FEATURE_ADVANCED_LOGIC);

  const nodeWithoutLogicDueToGroupPolicy =
    isInVanWestendorpGroup ||
    hasParentNodeWithEnabledShuffling ||
    inDisplaySamePageGroup;
  return {
    canAddLogic:
      isDisqualificationLogic ||
      (isNodeSupportingLogic && !nodeWithoutLogicDueToGroupPolicy),
  };
};

export function getOutcomePayload(logicMode, targetId, targetNodeType) {
  const outcomeAction = getDefaultOutcomeAction(
    logicMode,
    targetId,
    targetNodeType,
  );

  return {
    outcomeType: getOutcomeType(logicMode, outcomeAction?.valueType),
    outcomeAction,
  };
}

export function getDefaultConditionVisibility(node, isNodeLevel = true) {
  if (isNodeLevel) {
    return node?.refId;
  }

  // Answer level values are a multi-select: pre-selecting one option would
  // force the user to deselect it before picking what they actually want,
  // so it starts empty instead.
  return [];
}

export function getDefaultStatements(statements) {
  return statements.filter((statement) => !statement.rule);
}

export function getOutcomeParamsIdForNode(node) {
  if (node.groupRefId && !node.hasNext) {
    return FinalGroupNode;
  }

  return node.nextQuestionRefId;
}
